import UsageOverview from "@/components/dashboard/UsageOverview";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Pagination,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from "@heroui/react";
import NumberFlow from "@number-flow/react";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import {
  Award,
  ChevronLeft,
  ChevronRight,
  Crown,
  Eye,
  Medal,
  Trophy,
} from "lucide-react";
import { useEffect, useState } from "react";

import { getUserRanking } from "@/api/stats";
import { getMemberStats } from "@/api/user";

import { useHttpRequest } from "@/hooks/useHttpRequest";

export const Route = createFileRoute("/(authenticated)/dashboard")({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const [calandarType, setCalandarType] = useState<"yearly" | "monthly">(
    "yearly"
  );

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const currentYear = dayjs(selectedDate).year();
  const currentMonth = dayjs(selectedDate).month() + 1;

  const {
    data: memberStatsData,
    isLoading: memberStatsLoading,
    request: requestMemberStats,
  } = useHttpRequest(() => getMemberStats()?.then((res) => res.data));

  const {
    data: memberRankingData,
    isLoading: memberRankingLoading,
    request: requestMemberRanking,
  } = useHttpRequest(() =>
    getUserRanking(
      currentYear,
      calandarType === "monthly" ? currentMonth : undefined
    ).then((res) => res.data?.sort((a, b) => b.count - a.count))
  );

  const memberStats = memberStatsData || {
    totalCount: 0,
    invitedCount: 0,
    invitingCount: 0,
  };

  const getRankIcon = (rank: number) => {
    switch (rank) {
      case 1:
        return <Crown className="h-4 w-4 text-yellow-500" />;
      case 2:
        return <Trophy className="h-4 w-4 text-gray-400" />;
      case 3:
        return <Medal className="h-4 w-4 text-orange-500" />;
      default:
        return <Award className="h-4 w-4 text-default-400" />;
    }
  };

  const getRankTextColor = (rank: number) => {
    switch (rank) {
      case 1:
        return "text-yellow-600 dark:text-yellow-400";
      case 2:
        return "text-gray-600 dark:text-gray-400";
      case 3:
        return "text-orange-600 dark:text-orange-400";
      default:
        return "text-default-600";
    }
  };

  const paginatedRankingData = memberRankingData?.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil((memberRankingData?.length || 0) / itemsPerPage);

  const handleMonthChange = (direction: "prev" | "next") => {
    const unit = calandarType === "monthly" ? "month" : "year";
    const newDate = dayjs(selectedDate)
      .add(direction === "next" ? 1 : -1, unit)
      .toDate();
    setSelectedDate(newDate);
    setCurrentPage(1);
  };

  const handleUserClick = (uid: number) => {
    navigate({
      to: "/users/$id",
      params: {
        id: uid.toString(),
      },
    });
  };

  useEffect(() => {
    requestMemberStats();
  }, []);

  useEffect(() => {
    requestMemberRanking();
  }, [calandarType, currentYear, currentMonth]);

  useEffect(() => {
    setCurrentPage(1);
  }, [memberRankingData]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-default-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="space-y-8">
          {/* 멤버 현황 섹션 */}
          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-6">
              멤버 현황
            </h2>

            {/* 멤버 통계 카드 */}
            {memberStatsLoading ? (
              <div className="flex items-center justify-center p-8">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-default-500">로딩 중...</span>
                </div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-3 mb-6">
                <Card shadow="sm">
                  <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-default-600">
                          전체 멤버
                        </h4>
                        <NumberFlow
                          className="text-xl font-bold text-foreground"
                          value={memberStats?.totalCount ?? 0}
                        />
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card shadow="sm">
                  <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-default-600">
                          초대 완료
                        </h4>
                        <NumberFlow
                          className="text-xl font-bold text-foreground"
                          value={memberStats?.invitedCount ?? 0}
                        />
                      </div>
                    </div>
                  </CardBody>
                </Card>

                <Card shadow="sm">
                  <CardBody className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-default-600">
                          초대 중
                        </h4>
                        <NumberFlow
                          className="text-xl font-bold text-foreground"
                          value={memberStats?.invitingCount ?? 0}
                        />
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </div>
            )}

            {/* 날짜 및 캘린더 타입 선택기를 랭킹 카드 헤더로 이동 */}

            {/* 멤버 랭킹 */}
            <Card shadow="sm">
              <CardHeader className="pb-3 flex items-center justify-between gap-4">
                <h4 className="text-md font-semibold text-foreground">
                  {calandarType === "monthly"
                    ? dayjs(selectedDate).format("YYYY년 M월")
                    : dayjs(selectedDate).format("YYYY년")}{" "}
                  사용자 순위
                </h4>
                <div className="flex items-center gap-4">
                  <RadioGroup
                    orientation="horizontal"
                    value={calandarType}
                    onValueChange={(value) =>
                      setCalandarType(value as "yearly" | "monthly")
                    }
                    className="gap-x-4"
                  >
                    <Radio value="yearly">년도별</Radio>
                    <Radio value="monthly">월별</Radio>
                  </RadioGroup>

                  <Card className="p-2 w-fit" shadow="none">
                    <CardBody className="flex flex-row items-center gap-2 p-0">
                      <Button
                        isIconOnly
                        variant="light"
                        size="sm"
                        onPress={() => handleMonthChange("prev")}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <div className="px-2 text-center font-medium text-foreground">
                        {calandarType === "monthly"
                          ? dayjs(selectedDate).format("YYYY년 M월")
                          : dayjs(selectedDate).format("YYYY년")}
                      </div>
                      <Button
                        isIconOnly
                        variant="light"
                        size="sm"
                        isDisabled={dayjs(selectedDate).isSame(
                          dayjs(),
                          calandarType === "monthly" ? "month" : "year"
                        )}
                        onPress={() => handleMonthChange("next")}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </CardBody>
                  </Card>
                </div>
              </CardHeader>
              <CardBody className="pt-0">
                <Table
                  aria-label="사용자 순위 테이블"
                  isStriped
                  shadow="none"
                  bottomContent={
                    totalPages > 1 ? (
                      <div className="flex w-full justify-center">
                        <Pagination
                          isCompact
                          showControls
                          showShadow
                          color="primary"
                          page={currentPage}
                          total={totalPages}
                          onChange={setCurrentPage}
                        />
                      </div>
                    ) : null
                  }
                  classNames={{
                    wrapper: "border-none p-0",
                    base: "relative",
                  }}
                >
                  <TableHeader>
                    <TableColumn width={80}>순위</TableColumn>
                    <TableColumn>사용자 정보</TableColumn>
                    <TableColumn width={100} align="end">
                      질문 수
                    </TableColumn>
                    <TableColumn width={100} align="end">
                      <span className="hidden sm:inline">상세보기</span>
                    </TableColumn>
                  </TableHeader>
                  <TableBody
                    isLoading={memberRankingLoading}
                    loadingContent={
                      <div className="flex items-center justify-center p-8">
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-default-500">
                            랭킹 로딩 중...
                          </span>
                        </div>
                      </div>
                    }
                    emptyContent={
                      <div className="flex items-center justify-center p-8">
                        <div className="flex flex-col items-center gap-4">
                          <div className="w-16 h-16 rounded-full bg-default-100 flex items-center justify-center">
                            <Trophy className="h-8 w-8 text-default-400" />
                          </div>
                          <p className="text-default-500">
                            랭킹 데이터가 없습니다
                          </p>
                        </div>
                      </div>
                    }
                  >
                    {paginatedRankingData?.map((member, index) => {
                      const globalRank =
                        (currentPage - 1) * itemsPerPage + index + 1;
                      return (
                        <TableRow key={member.uid}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getRankIcon(globalRank)}
                              <span
                                className={`text-lg font-bold ${getRankTextColor(
                                  globalRank
                                )}`}
                              >
                                {globalRank}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col">
                              <h5 className="text-sm font-semibold text-foreground">
                                {member.name}
                              </h5>
                              <p className="text-xs text-default-500">
                                {member.email}
                              </p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 justify-end">
                              <NumberFlow
                                className="text-sm font-bold text-foreground"
                                value={member.count}
                              />
                              <span className="text-xs text-default-400">
                                회
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="flat"
                              size="sm"
                              onPress={() => handleUserClick(member.uid)}
                            >
                              <span className="hidden sm:inline">상세보기</span>
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    }) ?? []}
                  </TableBody>
                </Table>
              </CardBody>
            </Card>
          </div>

          {/* 질문수 현황 섹션 */}
          <UsageOverview />
        </div>
      </div>
    </div>
  );
}
